<template>
    <v-card elevation="0" class="payment-dialog">
        <div class="modal-close-btn">
            <v-btn icon @click="$emit('close')">
                <v-icon size="18" color="grey">mdi-close</v-icon>
            </v-btn>
        </div>

        <v-card-text class="pa-6 px-md-12">
            <form id="payment-form">
                <!-- Section: Your Details -->
                <div class="form-section">
                    <div class="section-header">
                        <v-icon color="primary" size="20" class="mr-2">mdi-account-outline</v-icon>
                        <h4 class="funds-title">Your Details</h4>
                    </div>

                    <v-row class="justify-center">
                        <v-col cols="12" md="6" class="pb-0">
                            <v-text-field
                                label="Full Name"
                                outlined
                                v-model="donation.donor_name"
                                :error-messages="nameErrors"
                                @input="$v.donation.donor_name.$touch()"
                                @blur="$v.donation.donor_name.$touch()"
                                :disabled="isProcessing"
                                class="btn-edu-height"
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="pb-0">
                            <v-text-field
                                label="Email Address"
                                outlined
                                v-model="donation.donor_email"
                                :error-messages="emailErrors"
                                @input="$v.donation.donor_email.$touch()"
                                @blur="$v.donation.donor_email.$touch()"
                                :disabled="isProcessing"
                                class="btn-edu-height"
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="pt-0">
                            <v-checkbox v-model="donation.is_anonymous" hide-details>
                                <template slot="label">
                                    <span class="checkbox-label">Show me as anonymous</span>
                                </template>
                            </v-checkbox>
                        </v-col>
                    </v-row>
                </div>

                <!-- Section: Payment Information -->
                <div class="form-section">
                    <div class="section-header">
                        <v-icon color="primary" size="20" class="mr-2">mdi-credit-card-outline</v-icon>
                        <h4 class="funds-title">Payment Information</h4>
                        <v-spacer></v-spacer>
                        <v-img
                            src="@/assets/icons/cards.png"
                            max-width="120"
                            min-width="120"
                            contain
                            class="card-brands"
                        ></v-img>
                    </div>

                    <v-row>
                        <v-col cols="12" class="pb-3">
                            <v-text-field
                                label="Name on Card"
                                outlined
                                v-model="donation.cardFullName"
                                :error-messages="cardNameErrors"
                                @input="$v.donation.cardFullName.$touch()"
                                @blur="$v.donation.cardFullName.$touch()"
                                :disabled="isProcessing"
                                class="btn-edu-height"
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" class="pb-2 pt-0">
                            <label class="card-input-label">
                                <v-icon size="16" color="#5D6E7F" class="mr-1">mdi-lock-outline</v-icon>
                                Card Details
                            </label>
                            <div class="card-element-wrapper">
                                <div id="card-element"></div>
                            </div>
                            <div id="card-errors" role="alert"></div>
                            <div class="secure-badge">
                                <v-icon size="12" color="#5cb85c">mdi-shield-check</v-icon>
                                <span>Secured by Stripe. Your card details are encrypted.</span>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Section: Summary -->
                <div class="form-section">
                    <div class="section-header">
                        <v-icon color="primary" size="20" class="mr-2">mdi-receipt-text-outline</v-icon>
                        <h4 class="funds-title">Summary</h4>
                    </div>

                    <v-card elevation="0" class="summary-card">
                        <div class="summary-row">
                            <span class="summary-label">Donation to {{ studentName }}</span>
                            <span class="summary-value">${{ amount }}</span>
                        </div>

                        <!-- Allocation breakdown -->
                        <template v-if="hasAllocations">
                            <div
                                v-for="allocation in allocationSummary"
                                :key="allocation.itemId"
                                class="summary-row allocation-row"
                            >
                                <span class="summary-label text-muted allocation-label">
                                    <v-icon x-small class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                                    {{ allocation.itemName }}
                                </span>
                                <span class="summary-value text-muted">${{ allocation.amount }}</span>
                            </div>
                            <div v-if="unallocatedAmount > 0" class="summary-row allocation-row">
                                <span class="summary-label text-muted allocation-label">
                                    <v-icon x-small class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                                    General fund
                                </span>
                                <span class="summary-value text-muted">${{ unallocatedAmount.toFixed(2) }}</span>
                            </div>
                        </template>

                        <div class="summary-row">
                            <span class="summary-label text-muted">Processing fee</span>
                            <span class="summary-value text-muted">${{ destinationFee }}</span>
                        </div>
                        <v-divider class="my-3"></v-divider>
                        <div class="summary-row summary-total">
                            <span class="summary-label">Total</span>
                            <span class="summary-value">${{ totalAmount }}</span>
                        </div>
                    </v-card>
                </div>

                <!-- Section: Note -->
                <div class="form-section">
                    <v-row>
                        <v-col class="pb-2 pt-0">
                            <label class="card-input-label">
                                <v-icon size="16" color="#5D6E7F" class="mr-1">mdi-message-text-outline</v-icon>
                                Leave a public note (optional)
                            </label>
                            <v-textarea
                                placeholder="Write an encouraging message..."
                                outlined
                                flat
                                solo
                                rows="2"
                                v-model="donation.donor_note"
                                :disabled="isProcessing"
                                no-resize
                                class="btn-edu-height mt-2"
                                hide-details="auto"
                            ></v-textarea>
                        </v-col>
                    </v-row>
                </div>

                <v-row>
                    <v-col class="pt-0">
                        <v-checkbox
                            v-model="isAgreedTermsAndConditions"
                            :error-messages="isAgreedTermsAndConditionsErrors"
                            @change="$v.isAgreedTermsAndConditions.$touch()"
                            hide-details="auto"
                        >
                            <template slot="label">
                                <span class="checkbox-label">
                                    I agree to the
                                    <a @click.stop="openTerms" class="terms-link">terms and conditions</a>
                                    of EDUrent
                                </span>
                            </template>
                        </v-checkbox>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="pt-4 d-flex justify-center">
                        <v-btn
                            color="primary"
                            @click="confirmCardPayment"
                            :loading="isProcessing"
                            depressed
                            class="btn-edurent donate-btn"
                            height="52"
                            block
                        >
                            <v-icon left size="20">mdi-heart-outline</v-icon>
                            Donate ${{ totalAmount }}
                        </v-btn>
                    </v-col>
                </v-row>
            </form>
        </v-card-text>
    </v-card>
</template>

<script>
import { email, required, sameAs } from 'vuelidate/lib/validators'
import CampaignService from '@/services/campaign.service'
import { calculateDestinationFee } from '@/utils/helpers'

export default {
    name: 'DonationPaymentModal',
    props: {
        campaign: {
            type: Object,
            required: true
        },
        amount: {
            type: [Number, String],
            required: true
        },
        clientSecret: {
            type: String,
            required: true
        },
        allocations: {
            type: Array,
            default: () => []
        }
    },
    validations: {
        donation: {
            donor_name: { required },
            donor_email: { email, required },
            cardFullName: { required }
        },
        isAgreedTermsAndConditions: { sameAs: sameAs(() => true) }
    },
    data: () => ({
        stripe: null,
        card: null,
        isProcessing: false,
        donation: {},
        isAgreedTermsAndConditions: false
    }),
    computed: {
        studentName() {
            return this.campaign?.user?.name || 'the student'
        },
        destinationFee() {
            return calculateDestinationFee(this.amount).toFixed(2)
        },
        totalAmount() {
            const fee = parseFloat(this.destinationFee)
            const base = parseFloat(this.amount)
            return (base + fee).toFixed(2)
        },
        hasAllocations() {
            return this.allocations && this.allocations.length > 0
        },
        allocationSummary() {
            if (!this.hasAllocations || !this.campaign?.items) return []
            return this.allocations.map(alloc => {
                const item = this.campaign.items.find(i => i.id === alloc.campaign_item_id)
                return {
                    itemId: alloc.campaign_item_id,
                    itemName: item?.name || 'Unknown item',
                    amount: (alloc.amount / 100).toFixed(2)
                }
            })
        },
        totalAllocated() {
            if (!this.hasAllocations) return 0
            return this.allocations.reduce((sum, a) => sum + (a.amount / 100), 0)
        },
        unallocatedAmount() {
            return parseFloat(this.amount) - this.totalAllocated
        },
        nameErrors() {
            const errors = []
            if (!this.$v.donation.donor_name.$dirty) return errors
            !this.$v.donation.donor_name.required && errors.push('Full name is required')
            if (this.$v.donation.donor_name.$model && !this.isFullName(this.$v.donation.donor_name.$model)) {
                errors.push('Please enter your first and last name')
            }
            return errors
        },
        emailErrors() {
            const errors = []
            if (!this.$v.donation.donor_email.$dirty) return errors
            if (this.$v.donation.donor_email.$model && !this.validEmail(this.$v.donation.donor_email.$model)) {
                errors.push('Please enter a valid email address')
            }
            !this.$v.donation.donor_email.required && errors.push('Email is required')
            return errors
        },
        cardNameErrors() {
            const errors = []
            if (!this.$v.donation.cardFullName.$dirty) return errors
            !this.$v.donation.cardFullName.required && errors.push('Name on card is required')
            return errors
        },
        isAgreedTermsAndConditionsErrors() {
            const errors = []
            if (!this.$v.isAgreedTermsAndConditions.$dirty) return errors
            !this.$v.isAgreedTermsAndConditions.sameAs && errors.push('You must agree to the terms and conditions')
            return errors
        }
    },
    methods: {
        isFullName(name) {
            return name && name.trim().split(' ').length > 1
        },
        validEmail(email) {
            const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            return re.test(email)
        },
        async confirmCardPayment() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.commit('setSnackbar', {
                    show: true,
                    message: 'Please fill in all required fields',
                    color: 'red'
                })
                return
            }

            this.isProcessing = true

            try {
                const response = await this.stripe.confirmCardPayment(this.clientSecret, {
                    payment_method: {
                        card: this.card,
                        billing_details: {
                            name: this.donation.cardFullName
                        }
                    }
                })

                if (response.error) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: response.error.message || 'Payment failed. Please try again.',
                        color: 'red'
                    })
                    this.isProcessing = false
                    return
                }

                if (response.paymentIntent.status !== 'succeeded') {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'Payment was not successful. Please try again.',
                        color: 'red'
                    })
                    this.isProcessing = false
                    return
                }

                // Store the donation record
                const donationData = {
                    user_id: this.campaign.user_id,
                    campaign_id: this.campaign.id,
                    donor_name: this.donation.donor_name,
                    donor_email: this.donation.donor_email,
                    is_anonymous: this.donation.is_anonymous,
                    donor_note: this.donation.donor_note || '',
                    amount: this.amount * 100,
                    destination_fee: Math.round(parseFloat(this.destinationFee) * 100)
                }

                await CampaignService.storeDonation(donationData, this.allocations)

                this.resetForm()
                this.$emit('paymentCompleted')
            } catch (error) {
                this.$store.commit('setSnackbar', {
                    show: true,
                    message: 'An error occurred while processing your payment. Please try again.',
                    color: 'red'
                })
            } finally {
                this.isProcessing = false
            }
        },
        initDonation() {
            this.$v.$reset()
            this.donation = {
                donor_name: '',
                donor_email: '',
                is_anonymous: false,
                cardFullName: '',
                donor_note: ''
            }
            this.isAgreedTermsAndConditions = false
        },
        resetForm() {
            this.initDonation()
        },
        openTerms() {
            const route = this.$router.resolve({ name: 'Terms' })
            window.open(route.href, '_blank')
        },
        initStripe() {
            this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY)

            const elements = this.stripe.elements()
            const style = {
                base: {
                    color: '#32325d',
                    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                    fontSmoothing: 'antialiased',
                    fontSize: '16px',
                    '::placeholder': {
                        color: '#aab7c4'
                    }
                },
                invalid: {
                    color: '#fa755a',
                    iconColor: '#fa755a'
                }
            }

            this.card = elements.create('card', { style })
            this.card.mount('#card-element')

            this.card.on('change', (event) => {
                const displayError = document.getElementById('card-errors')
                if (event.error) {
                    displayError.textContent = event.error.message
                } else {
                    displayError.textContent = ''
                }
            })
        }
    },
    beforeMount() {
        this.initDonation()
    },
    mounted() {
        this.initStripe()
    }
}
</script>

<style scoped>
.payment-dialog {
    border-radius: 12px !important;
}

/* Section styling */
.form-section {
    margin-bottom: 24px;
    padding-bottom: 8px;
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eef2f6;
}

.section-header h4 {
    margin: 0;
}

.payment-dialog h4.funds-title {
    font-size: 16px !important;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
}

/* Card element styling */
.card-input-label {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 500;
    color: #5D6E7F;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.card-element-wrapper {
    background: linear-gradient(to bottom, #fafbfc, #ffffff);
    border: 1px solid #d1d9e0;
    border-radius: 8px;
    padding: 4px;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.card-element-wrapper:hover {
    border-color: #a8b5c4;
}

.card-element-wrapper:focus-within {
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

#card-element {
    box-sizing: border-box;
    height: 48px;
    padding: 14px 12px;
    border: none;
    border-radius: 6px;
    background-color: white;
}

#card-errors {
    color: #e53935;
    font-size: 12px;
    margin-top: 6px;
    display: flex;
    align-items: center;
}

.secure-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    font-size: 11px;
    color: #5cb85c;
}

.secure-badge span {
    color: #6b7c8f;
}

/* Summary card */
.summary-card {
    background: #f8fafc !important;
    border: 1px solid #eef2f6 !important;
    border-radius: 10px !important;
    padding: 16px 20px;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
}

.summary-label {
    font-size: 14px;
    color: #1a1a2e;
}

.summary-value {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
}

.summary-row.summary-total .summary-label,
.summary-row.summary-total .summary-value {
    font-size: 16px;
    font-weight: 700;
    color: #1976d2;
}

.text-muted {
    color: #6b7c8f !important;
    font-weight: 400 !important;
}

.allocation-row {
    padding-left: 8px;
}

.allocation-label {
    font-size: 13px;
}

/* Checkbox and links */
.checkbox-label {
    font-size: 13px;
    color: #5D6E7F;
}

.terms-link {
    color: #1976d2;
    text-decoration: none;
    font-weight: 500;
}

.terms-link:hover {
    text-decoration: underline;
}

/* Donate button */
.donate-btn {
    font-size: 16px !important;
    font-weight: 600 !important;
    letter-spacing: 0.3px;
    border-radius: 10px !important;
    text-transform: none !important;
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
    transition: all 0.2s ease !important;
}

.donate-btn:hover {
    box-shadow: 0 6px 16px rgba(25, 118, 210, 0.4) !important;
    transform: translateY(-1px);
}

/* Card brands image */
.card-brands {
    opacity: 0.7;
}

/* Responsive */
@media (max-width: 600px) {
    .section-header {
        flex-wrap: wrap;
    }

    .card-brands {
        margin-top: 8px;
        width: 100%;
    }
}
</style>